import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { getUpcomingEvents } from '../../services/scheduleService';
import fieldNewsService, { FieldNewsDto } from '../../services/fieldNewsService';
import { FieldScheduleEntry, formatTime, ENTRY_TYPE_COLORS } from '../../types/schedule';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const SectionWrapper = styled.div<{ $night?: boolean }>`
  background: ${props => props.$night ? 'rgba(0, 255, 136, 0.03)' : 'rgba(0, 0, 0, 0.3)'};
  border: 1px solid ${props => props.$night ? 'rgba(0, 255, 136, 0.2)' : 'rgba(0, 212, 255, 0.2)'};
  border-radius: 15px;
  overflow: hidden;
`;

const SectionHeader = styled.div<{ $night?: boolean }>`
  background: ${props => props.$night ? 'rgba(0, 255, 136, 0.1)' : 'rgba(0, 212, 255, 0.1)'};
  padding: 15px 20px;
  border-bottom: 1px solid ${props => props.$night ? 'rgba(0, 255, 136, 0.2)' : 'rgba(0, 212, 255, 0.2)'};
`;

const SectionTitle = styled.h3<{ $night?: boolean }>`
  color: ${props => props.$night ? '#00ff88' : '#00d4ff'};
  margin: 0;
  font-size: 1.1rem;
  font-family: 'Orbitron', monospace;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const SectionContent = styled.div<{ $night?: boolean }>`
  padding: 20px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EventCard = styled.div<{ $isHappening?: boolean }>`
  background: rgba(76, 175, 80, 0.05);
  border: 1px solid ${props => props.$isHappening ? '#4CAF50' : 'rgba(76, 175, 80, 0.3)'};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;

  ${props => props.$isHappening && `
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.2);
  `}

  &:hover {
    background: rgba(76, 175, 80, 0.1);
    border-color: #4CAF50;
    transform: translateY(-2px);
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const EventInfo = styled.div`
  flex: 1;
`;

const EventTitle = styled.h4`
  color: #4CAF50;
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const EventDateTime = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #b0b0b0;
  font-size: 0.9rem;
  margin-bottom: 8px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const DateBadge = styled.span`
  background: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
`;

const TimeBadge = styled.span`
  color: #ccc;
`;

const HappeningNowBadge = styled.span`
  background: linear-gradient(135deg, #4CAF50, #2E7D32);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  animation: ${pulse} 2s ease-in-out infinite;
  white-space: nowrap;
`;

const EventDescription = styled.p`
  color: #ccc;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 12px 0;
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: #888;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RefereeNames = styled.span`
  color: #00d4ff;
`;

const GameTypeBadge = styled.span<{ $gameType: string }>`
  background: ${props => {
    switch (props.$gameType) {
      case 'airsoft': return 'rgba(76, 175, 80, 0.2)';
      case 'paintball': return 'rgba(255, 152, 0, 0.2)';
      case 'speedball': return 'rgba(156, 39, 176, 0.2)';
      default: return 'rgba(158, 158, 158, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$gameType) {
      case 'airsoft': return '#4CAF50';
      case 'paintball': return '#FF9800';
      case 'speedball': return '#9C27B0';
      default: return '#9E9E9E';
    }
  }};
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  background: ${props => props.$variant === 'secondary'
    ? 'transparent'
    : 'linear-gradient(135deg, #4CAF50, #2E7D32)'};
  border: 1px solid ${props => props.$variant === 'secondary' ? '#00d4ff' : 'transparent'};
  color: ${props => props.$variant === 'secondary' ? '#00d4ff' : 'white'};
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.$variant === 'secondary'
      ? '0 4px 15px rgba(0, 212, 255, 0.3)'
      : '0 4px 15px rgba(76, 175, 80, 0.3)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 30px 20px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;

  &::after {
    content: '';
    width: 30px;
    height: 30px;
    border: 3px solid rgba(76, 175, 80, 0.3);
    border-radius: 50%;
    border-top-color: #4CAF50;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface UpcomingEventsSectionProps {
  fieldId: string;
  onViewSchedule: () => void;
  onNewsClick?: (news: FieldNewsDto) => void;
  $night?: boolean;
}

interface EventWithNews extends FieldScheduleEntry {
  linkedNews?: FieldNewsDto | null;
}

const UpcomingEventsSection: React.FC<UpcomingEventsSectionProps> = ({
  fieldId,
  onViewSchedule,
  onNewsClick,
  $night = false
}) => {
  const [events, setEvents] = useState<EventWithNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUpcomingEvents();
  }, [fieldId]);

  const loadUpcomingEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get upcoming events
      const upcomingEvents = await getUpcomingEvents(fieldId, 3);

      // Load linked news for each event
      const eventsWithNews: EventWithNews[] = await Promise.all(
        upcomingEvents.map(async (event) => {
          try {
            const news = await fieldNewsService.getNewsByEventId(event.id);
            return { ...event, linkedNews: news };
          } catch {
            return { ...event, linkedNews: null };
          }
        })
      );

      setEvents(eventsWithNews);
    } catch (err: any) {
      console.error('Failed to load upcoming events:', err);
      setError('Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const isEventHappening = (event: FieldScheduleEntry): boolean => {
    const now = new Date();
    // Parse date manually to avoid timezone issues (YYYY-MM-DD)
    const [year, month, day] = event.date.split('-').map(Number);
    const eventDate = new Date(year, month - 1, day);

    // Parse start and end times
    const startParts = event.startTime.split('T')[1]?.substring(0, 5).split(':') || ['0', '0'];
    const endParts = event.endTime.split('T')[1]?.substring(0, 5).split(':') || ['23', '59'];

    const startTime = new Date(eventDate);
    startTime.setHours(parseInt(startParts[0]), parseInt(startParts[1]), 0);

    const endTime = new Date(eventDate);
    endTime.setHours(parseInt(endParts[0]), parseInt(endParts[1]), 0);

    return now >= startTime && now <= endTime;
  };

  const formatEventDate = (dateStr: string): string => {
    // Parse date manually to avoid timezone issues (YYYY-MM-DD)
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) {
      return 'Today';
    } else if (date.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  // Don't show anything while loading, on error, or if no events
  if (loading || error || events.length === 0) {
    return null;
  }

  return (
    <SectionWrapper $night={$night}>
      <SectionHeader $night={$night}>
        <SectionTitle $night={$night}>Upcoming Events</SectionTitle>
      </SectionHeader>
      <SectionContent $night={$night}>
        <Container>
          {events.map((event) => {
            const isHappening = isEventHappening(event);

            return (
              <EventCard key={event.id} $isHappening={isHappening}>
                <EventHeader>
                  <EventInfo>
                    <EventTitle>{event.title || 'Untitled Event'}</EventTitle>
                    <EventDateTime>
                      <DateBadge>{formatEventDate(event.date)}</DateBadge>
                      <TimeBadge>
                        {formatTime(event.startTime)} - {formatTime(event.endTime)}
                      </TimeBadge>
                    </EventDateTime>
                  </EventInfo>
                  {isHappening && (
                    <HappeningNowBadge>Happening Now</HappeningNowBadge>
                  )}
                </EventHeader>

                {event.description && (
                  <EventDescription>{event.description}</EventDescription>
                )}

                <EventMeta>
                  {event.gameType && (
                    <MetaItem>
                      <GameTypeBadge $gameType={event.gameType}>{event.gameType}</GameTypeBadge>
                    </MetaItem>
                  )}
                  {event.refereeNames && event.refereeNames.length > 0 && (
                    <MetaItem>
                      Referees: <RefereeNames>{event.refereeNames.join(', ')}</RefereeNames>
                    </MetaItem>
                  )}
                </EventMeta>

                <ButtonsContainer>
                  {event.linkedNews && onNewsClick && (
                    <ActionButton onClick={() => onNewsClick(event.linkedNews!)}>
                      Read News
                    </ActionButton>
                  )}
                  <ActionButton $variant="secondary" onClick={onViewSchedule}>
                    View Schedule
                  </ActionButton>
                </ButtonsContainer>
              </EventCard>
            );
          })}
        </Container>
      </SectionContent>
    </SectionWrapper>
  );
};

export default UpcomingEventsSection;
